import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ArrowDown,
  ArrowUp,
  Braces,
  Info,
  ListChecks,
  Plus,
  Sigma,
  Trash2,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { InputDropdown } from '@/components/ui/input-dropdown';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import { useOrgStore } from '@/stores/org-store';
import type { CustomNode, NodeStruct } from '@/types/node-types';
import {
  getSwitchConfig,
  validateExpressionAst,
  validateSwitchConfig,
  type ExpressionAst,
  type JsonValue,
  type SwitchCase,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  astToCondition,
  builderToCases,
  casesToBuilder,
  conditionToFormula,
  describeCondition,
  elementKindOf,
  fieldFromText,
  fieldSuggestionsForSample,
  formatFieldPath,
  formatListLiteral,
  formatTypedList,
  isBuilderRepresentable,
  newRule,
  operatorCanGuardMissing,
  operatorTakesList,
  operatorTakesValue,
  OPERATOR_LABELS,
  parseListLiteral,
  parseTypedList,
  parseTypedValue,
  RULE_OPERATORS,
  VALUE_ROOT,
  valueKindOf,
  type BuilderCase,
  type Condition,
  type ElementKind,
  type RuleOperator,
  type ValueKind,
} from '@/pages/workflow/helper/switch-condition-builder';
import { previewSwitchSelection } from '@/pages/workflow/helper/expression-eval';
import { previewExpressions } from '@/services/workflow/expressions-preview';
import {
  FUNCTION_SUGGESTIONS,
  FormulaInput,
  fieldSuggestionsToFormula,
  type FormulaProblem,
} from './formula-input';
import {
  edgesForBranch,
  orphanedBranchEdges,
  resolveSwitchInputSample,
} from '@/pages/workflow/helper/switch-branch-sync';
import { JsonEditorField } from './json-editor-field';
import { cn } from '@/lib/utils';

// --- small shared pieces ---------------------------------------------------

const VALUE_KIND_LABELS: Record<ValueKind, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'True / false',
  null: 'Null',
  list: 'List',
};
const VALUE_KINDS = Object.keys(VALUE_KIND_LABELS) as ValueKind[];
/** Item kinds for membership ("is one of") lists: no nested lists there. */
const ITEM_KINDS: ValueKind[] = ['text', 'number', 'boolean', 'null'];
const kindFromLabel = (label: string | undefined): ValueKind =>
  VALUE_KINDS.find((kind) => VALUE_KIND_LABELS[kind] === label) ?? 'text';

const ELEMENT_KIND_LABELS: Record<ElementKind, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'True / false',
  null: 'Null',
  json: 'Mixed / JSON',
};
const ELEMENT_KINDS = Object.keys(ELEMENT_KIND_LABELS) as ElementKind[];
const elementKindFromLabel = (label: string | undefined): ElementKind =>
  ELEMENT_KINDS.find((kind) => ELEMENT_KIND_LABELS[kind] === label) ?? 'text';

const OPERATOR_OPTIONS = RULE_OPERATORS.map((op) => OPERATOR_LABELS[op]);
const operatorFromLabel = (label: string | undefined): RuleOperator =>
  RULE_OPERATORS.find((op) => OPERATOR_LABELS[op] === label) ?? 'eq';

const selectClass = 'h-8 w-auto min-w-0 text-xs px-2';

/** Text input that keeps a local draft and commits on blur / Enter. */
/** Shared with the Validate builder (Phase 2). */
export function DraftInput({
  value,
  onCommit,
  className,
  ...rest
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
  value: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Input
      {...rest}
      value={draft}
      className={cn('h-8 text-xs', className)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => draft !== value && onCommit(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

// --- rule row --------------------------------------------------------------

interface RuleRowProps {
  /** --- if/else (Phase 8b): the If has its own fallback control, so the
   * per-rule "treat a missing field as no match" checkbox is hidden. --- */
  hideMissingGuard?: boolean;
  rule: Extract<Condition, { kind: 'rule' }>;
  suggestions: string[];
  disabled: boolean;
  onChange: (next: Condition) => void;
  /** Rewrite this rule as one line of formula text (Phase 8a). */
  onFormula?: () => void;
}

function RuleRow({
  rule,
  suggestions,
  disabled,
  onChange,
  hideMissingGuard = false,
  onFormula,
}: RuleRowProps) {
  const [fieldDraft, setFieldDraft] = useState(formatFieldPath(rule.field));
  useEffect(() => setFieldDraft(formatFieldPath(rule.field)), [rule.field]);

  const takesValue = operatorTakesValue(rule.op);
  const takesList = operatorTakesList(rule.op);
  const [kind, setKind] = useState<ValueKind>(() =>
    takesList
      ? Array.isArray(rule.value) && rule.value.length > 0
        ? valueKindOf(rule.value[0])
        : 'text'
      : valueKindOf(rule.value),
  );
  // For a literal list compared with equals / does not equal: what its
  // items are (Phase 2). The algorithm suite compares list outputs this way.
  const [elementKind, setElementKind] = useState<ElementKind>(() =>
    elementKindOf(rule.value),
  );
  const isListValue = !takesList && kind === 'list';
  const draftFor = (value: JsonValue, element: ElementKind): string =>
    takesList
      ? formatTypedList(value)
      : Array.isArray(value)
        ? formatListLiteral(value, element)
        : value === null
          ? ''
          : String(value);
  const [valueDraft, setValueDraft] = useState(() =>
    draftFor(rule.value, elementKind),
  );
  const [valueError, setValueError] = useState<string | null>(null);
  useEffect(() => {
    // 'Mixed / JSON' can show any list, so an explicit choice of it is kept;
    // otherwise the item kind follows the committed value.
    const nextElement =
      Array.isArray(rule.value) && elementKind !== 'json'
        ? elementKindOf(rule.value)
        : elementKind;
    if (nextElement !== elementKind) setElementKind(nextElement);
    setValueDraft(draftFor(rule.value, nextElement));
    setValueError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync the draft only when the committed value changes
  }, [rule.value, takesList]);

  const commitField = () => {
    const parsed = fieldFromText(fieldDraft);
    if (formatFieldPath(parsed) !== formatFieldPath(rule.field)) {
      onChange({ ...rule, field: parsed });
    }
  };

  const commitListLiteral = (text: string, element: ElementKind) => {
    const parsed = parseListLiteral(text, element);
    if (!parsed) {
      setValueError(
        element === 'json'
          ? 'Each item must be valid JSON, e.g. "a", 1, true or {"k": 1}, separated by commas or one per line.'
          : element === 'number'
            ? 'Enter numbers separated by commas or one per line.'
            : element === 'boolean'
              ? 'Enter true or false values separated by commas or one per line.'
              : 'Enter values separated by commas or one per line.',
      );
      return;
    }
    setValueError(null);
    if (JSON.stringify(parsed.value) !== JSON.stringify(rule.value)) {
      onChange({ ...rule, value: parsed.value });
    }
  };

  const commitValue = (text: string, nextKind: ValueKind = kind) => {
    if (!takesList && nextKind === 'list') {
      commitListLiteral(text, elementKind);
      return;
    }
    if (takesList) {
      const parsed = parseTypedList(text, nextKind);
      if (!parsed) {
        setValueError(
          nextKind === 'number'
            ? 'Enter numbers separated by commas.'
            : 'Enter true or false values separated by commas.',
        );
        return;
      }
      setValueError(null);
      onChange({ ...rule, value: parsed.value });
      return;
    }
    const parsed = parseTypedValue(text, nextKind);
    if (!parsed) {
      setValueError(
        nextKind === 'number' ? 'Enter a number.' : 'Enter true or false.',
      );
      return;
    }
    setValueError(null);
    if (JSON.stringify(parsed.value) !== JSON.stringify(rule.value)) {
      onChange({ ...rule, value: parsed.value });
    }
  };

  const changeOperator = (next: RuleOperator) => {
    const nextTakesList = operatorTakesList(next);
    let value: JsonValue = rule.value;
    if (nextTakesList && !Array.isArray(value)) {
      value = value === null || value === '' ? [] : [value];
    } else if (!nextTakesList && Array.isArray(value)) {
      value = value[0] ?? '';
    }
    if (!operatorTakesValue(next)) value = null;
    onChange({
      ...rule,
      op: next,
      value,
      guardMissing: operatorCanGuardMissing(next) ? rule.guardMissing : false,
    });
  };

  return (
    <div className='flex flex-col gap-1.5' data-testid='switch-rule'>
      <div className='flex flex-wrap items-center gap-1.5'>
        <InputDropdown
          value={fieldDraft}
          options={suggestions.map((path) => ({ value: path, label: path }))}
          placeholder='field (e.g. verdict or rating.total)'
          aria-label='Field'
          data-testid='switch-rule-field'
          disabled={disabled}
          className='h-8 text-xs min-w-[150px] flex-1'
          onChange={(text) => setFieldDraft(text)}
          onSelect={(option) => {
            const parsed = fieldFromText(option.value);
            setFieldDraft(formatFieldPath(parsed));
            onChange({ ...rule, field: parsed });
          }}
          onBlur={commitField}
        />
        <Select
          options={OPERATOR_OPTIONS}
          selected={OPERATOR_LABELS[rule.op]}
          allowAllOption={false}
          disabled={disabled}
          className={cn(selectClass, 'min-w-[128px]')}
          onSelect={(label) => changeOperator(operatorFromLabel(label))}
        />
        {takesValue && (
          <>
            {isListValue ? (
              <Textarea
                value={valueDraft}
                aria-label='List values'
                data-testid='switch-rule-value'
                placeholder='a, b, c (or one per line)'
                disabled={disabled}
                rows={valueDraft.includes('\n') ? 3 : 1}
                className={cn(
                  // Its own row: lists read better at full width.
                  'order-last basis-full min-h-8 py-1.5 text-xs font-mono resize-y',
                  valueError && 'border-red-500',
                )}
                onChange={(event) => setValueDraft(event.target.value)}
                onBlur={() => commitListLiteral(valueDraft, elementKind)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    commitListLiteral(valueDraft, elementKind);
                  }
                }}
              />
            ) : kind === 'boolean' && !takesList ? (
              <Select
                options={['true', 'false']}
                selected={valueDraft === 'false' ? 'false' : 'true'}
                allowAllOption={false}
                disabled={disabled}
                className={cn(selectClass, 'min-w-[80px]')}
                onSelect={(choice) => commitValue(choice ?? 'true')}
              />
            ) : kind === 'null' ? null : (
              <Input
                value={valueDraft}
                aria-label={takesList ? 'Values' : 'Value'}
                data-testid='switch-rule-value'
                placeholder={takesList ? 'a, b, c' : 'value'}
                disabled={disabled}
                className={cn(
                  'h-8 text-xs min-w-[110px] flex-1',
                  valueError && 'border-red-500',
                )}
                onChange={(event) => setValueDraft(event.target.value)}
                onBlur={() => commitValue(valueDraft)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitValue(valueDraft);
                  }
                }}
              />
            )}
            <Select
              options={(takesList ? ITEM_KINDS : VALUE_KINDS).map(
                (k) => VALUE_KIND_LABELS[k],
              )}
              selected={VALUE_KIND_LABELS[kind]}
              allowAllOption={false}
              disabled={disabled}
              aria-label='Value kind'
              className={cn(selectClass, 'min-w-[96px]')}
              onSelect={(label) => {
                const nextKind = kindFromLabel(label);
                setKind(nextKind);
                if (nextKind === 'list') {
                  // Carry the current single value over as the first item.
                  const seed: JsonValue[] =
                    rule.value === null || rule.value === ''
                      ? []
                      : Array.isArray(rule.value)
                        ? rule.value
                        : [rule.value];
                  const element = elementKindOf(seed);
                  setElementKind(element);
                  setValueDraft(formatListLiteral(seed, element));
                  setValueError(null);
                  if (JSON.stringify(seed) !== JSON.stringify(rule.value)) {
                    onChange({ ...rule, value: seed });
                  }
                } else if (kind === 'list' && Array.isArray(rule.value)) {
                  // Back to a single value: keep the first item.
                  const first = rule.value[0];
                  const text =
                    first === undefined || first === null
                      ? ''
                      : typeof first === 'string'
                        ? first
                        : JSON.stringify(first);
                  setValueDraft(text);
                  commitValue(
                    nextKind === 'boolean'
                      ? text === 'false'
                        ? 'false'
                        : 'true'
                      : text,
                    nextKind,
                  );
                } else if (nextKind === 'null') {
                  commitValue('', 'null');
                } else if (nextKind === 'boolean') {
                  commitValue(
                    valueDraft === 'false' ? 'false' : 'true',
                    'boolean',
                  );
                } else {
                  commitValue(valueDraft, nextKind);
                }
              }}
            />
            {isListValue && (
              <Select
                options={ELEMENT_KINDS.map((k) => ELEMENT_KIND_LABELS[k])}
                selected={ELEMENT_KIND_LABELS[elementKind]}
                allowAllOption={false}
                disabled={disabled}
                aria-label='List item kind'
                className={cn(selectClass, 'min-w-[110px]')}
                onSelect={(label) => {
                  const next = elementKindFromLabel(label);
                  setElementKind(next);
                  commitListLiteral(valueDraft, next);
                }}
              />
            )}
          </>
        )}
        {onFormula && !disabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 px-1.5 text-[11px] text-muted-foreground'
            aria-label='Write as formula'
            title='Write this condition as a formula (e.g. n % 15 == 0)'
            data-testid='switch-rule-to-formula'
            onClick={onFormula}
          >
            <Sigma className='size-3' />
          </Button>
        )}
      </div>
      {valueError && <p className='text-xs text-red-500'>{valueError}</p>}
      {!hideMissingGuard && operatorCanGuardMissing(rule.op) && (
        <label className='flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer'>
          <Checkbox
            checked={rule.guardMissing}
            disabled={disabled}
            aria-label='If the field is missing, treat as no match'
            onCheckedChange={(checked) =>
              onChange({ ...rule, guardMissing: checked === true })
            }
          />
          <span>
            If the field is missing, treat as no match
            {!rule.guardMissing && ' (otherwise the run fails)'}
          </span>
        </label>
      )}
    </div>
  );
}

// --- condition editor (recursive) -----------------------------------------

export interface ConditionEditorProps {
  condition: Condition;
  suggestions: string[];
  disabled: boolean;
  depth: number;
  onChange: (next: Condition) => void;
  /** --- if/else (Phase 8b): hide the per-rule missing-field checkbox --- */
  hideMissingGuard?: boolean;
  /** Context key bare names belong to: `value` (default) or `judge` for a loop exit. */
  root?: string;
}

interface FormulaConditionRowProps {
  condition: Extract<Condition, { kind: 'formula' }>;
  suggestions: string[];
  disabled: boolean;
  root: string;
  onChange: (next: Condition) => void;
}

/**
 * One line of formula text as a condition (`n % 15 == 0`, `len(items) > 3`).
 * The backend parses it (the only parser); a problem is shown with its
 * position and the previous tree is kept until the text parses.
 */
function FormulaConditionRow({
  condition,
  suggestions,
  disabled,
  root,
  onChange,
}: FormulaConditionRowProps) {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const [problem, setProblem] = useState<FormulaProblem | null>(null);
  const [checking, setChecking] = useState(false);
  const formulaSuggestions = useMemo(
    () => [...fieldSuggestionsToFormula(suggestions), ...FUNCTION_SUGGESTIONS],
    [suggestions],
  );
  const asFields = useMemo(
    () => astToCondition(condition.ast, root),
    [condition.ast, root],
  );
  const canShowFields =
    asFields.kind !== 'formula' && asFields.kind !== 'advanced';

  const commit = async (text: string) => {
    if (!text.trim()) {
      setProblem({ message: 'Write a condition, e.g. n % 15 == 0.' });
      onChange({ kind: 'formula', text, ast: { op: 'literal', value: false } });
      return;
    }
    if (!orgId) {
      setProblem({
        message: 'The formula cannot be checked without an organisation.',
      });
      return;
    }
    setChecking(true);
    try {
      const response = await previewExpressions(orgId, {
        formulas: [{ formula: text }],
        root,
        mode: 'condition',
      });
      const result = response.results[0];
      if (!result || result.error || !result.expression) {
        setProblem(
          result?.error
            ? {
                message: result.error.message,
                hint: result.error.hint,
                position: result.error.position,
              }
            : { message: 'The formula could not be parsed.' },
        );
        return;
      }
      setProblem(null);
      onChange({ kind: 'formula', text, ast: result.expression });
    } catch (error) {
      setProblem({
        message:
          error instanceof Error
            ? error.message
            : 'The formula could not be checked.',
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className='space-y-1' data-testid='switch-formula-condition'>
      <div className='flex items-start gap-1.5'>
        <Sigma className='size-3.5 mt-2 shrink-0 text-muted-foreground' />
        <FormulaInput
          value={condition.text}
          suggestions={formulaSuggestions}
          disabled={disabled}
          placeholder='formula, e.g. n % 15 == 0 or len(items) > 3'
          aria-label='Condition formula'
          data-testid='switch-formula-input'
          problem={problem}
          onCommit={(text) => void commit(text)}
        />
      </div>
      <div className='flex items-center justify-between gap-2 pl-5 text-[11px] text-muted-foreground'>
        <span>
          {checking
            ? 'Checking…'
            : 'Fields, + - * / % ^, ~ to join text, == != < <= > >=, and/or/not, in, and functions like round(), len(), any().'}
        </span>
        {!disabled && (
          <div className='flex gap-1 shrink-0'>
            {canShowFields && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-6 px-2 text-xs'
                data-testid='switch-formula-to-fields'
                onClick={() => onChange(asFields)}
              >
                Show as fields
              </Button>
            )}
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-2 text-xs'
              onClick={() => onChange({ kind: 'advanced', ast: condition.ast })}
            >
              Edit as JSON
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared with the Validate builder (Phase 2, the same grammar over `value`)
 * and the Repeat Until panel (Phase 3, which builds its stop condition over
 * the judge's outputs with the same grammar). */
export function ConditionEditor({
  condition,
  suggestions,
  disabled,
  depth,
  onChange,
  hideMissingGuard = false,
  root = VALUE_ROOT,
}: ConditionEditorProps) {
  if (condition.kind === 'rule') {
    return (
      <RuleRow
        rule={condition}
        suggestions={suggestions}
        disabled={disabled}
        onChange={onChange}
        hideMissingGuard={hideMissingGuard}
        onFormula={() => onChange(conditionToFormula(condition, root))}
      />
    );
  }
  if (condition.kind === 'formula') {
    return (
      <FormulaConditionRow
        condition={condition}
        suggestions={suggestions}
        disabled={disabled}
        root={root}
        onChange={onChange}
      />
    );
  }
  if (condition.kind === 'always') {
    return (
      <div className='flex items-center justify-between gap-2 text-xs'>
        <span className='text-muted-foreground'>
          {condition.value
            ? 'Always matches (every value takes this branch).'
            : 'Never matches.'}
        </span>
        {!disabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => onChange(newRule())}
          >
            Add a condition
          </Button>
        )}
      </div>
    );
  }
  if (condition.kind === 'advanced') {
    const converted = astToCondition(condition.ast, root);
    const representable = isBuilderRepresentable(condition.ast, root);
    return (
      <div className='space-y-1' data-testid='switch-advanced-condition'>
        <div className='flex items-center justify-between text-[11px] text-muted-foreground'>
          <span>
            Custom expression (not editable as fields; JSON is kept exactly).
          </span>
          {converted.kind !== 'advanced' && !disabled && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-2 text-xs'
              onClick={() => onChange(converted)}
            >
              {representable ? 'Show as fields' : 'Show as formula'}
            </Button>
          )}
        </div>
        <JsonEditorField
          value={condition.ast}
          rows={5}
          expectedShape='object'
          disabled={disabled}
          aria-label='Custom expression'
          validate={(parsed) =>
            validateExpressionAst(parsed)[0]?.message ?? null
          }
          onCommit={(parsed) =>
            onChange({ kind: 'advanced', ast: parsed as ExpressionAst })
          }
        />
      </div>
    );
  }
  // group
  const groupLabel = condition.op === 'and' ? 'All of these' : 'Any of these';
  return (
    <div
      className={cn(
        'rounded-md border border-dashed p-2 space-y-2',
        depth > 0 && 'bg-muted/30',
      )}
      data-testid='switch-condition-group'
    >
      <div className='flex items-center gap-2'>
        <Select
          options={['All of these', 'Any of these']}
          selected={groupLabel}
          allowAllOption={false}
          disabled={disabled}
          className={cn(selectClass, 'min-w-[110px]')}
          onSelect={(choice) =>
            onChange({
              ...condition,
              op: choice === 'Any of these' ? 'or' : 'and',
            })
          }
        />
        <span className='text-[11px] text-muted-foreground'>must be true</span>
      </div>
      {condition.items.map((item, index) => (
        <div key={index} className='flex items-start gap-1'>
          <div className='flex-1 min-w-0'>
            <ConditionEditor
              condition={item}
              suggestions={suggestions}
              disabled={disabled}
              depth={depth + 1}
              hideMissingGuard={hideMissingGuard}
              root={root}
              onChange={(next) =>
                onChange({
                  ...condition,
                  items: condition.items.map((entry, i) =>
                    i === index ? next : entry,
                  ),
                })
              }
            />
          </div>
          {!disabled && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0 shrink-0 text-muted-foreground'
              aria-label='Remove condition'
              onClick={() => {
                const items = condition.items.filter((_, i) => i !== index);
                if (items.length === 1) onChange(items[0]);
                else onChange({ ...condition, items });
              }}
            >
              <Trash2 className='size-3.5' />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <div className='flex gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() =>
              onChange({ ...condition, items: [...condition.items, newRule()] })
            }
          >
            <Plus className='size-3' /> condition
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            data-testid='switch-group-add-formula'
            onClick={() =>
              onChange({
                ...condition,
                items: [
                  ...condition.items,
                  {
                    kind: 'formula',
                    text: '',
                    ast: { op: 'literal', value: false },
                  },
                ],
              })
            }
          >
            <Sigma className='size-3' /> formula
          </Button>
          {depth < 3 && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-2 text-xs'
              onClick={() =>
                onChange({
                  ...condition,
                  items: [
                    ...condition.items,
                    { kind: 'group', op: 'or', items: [newRule()] },
                  ],
                })
              }
            >
              <Plus className='size-3' /> group
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// --- one case --------------------------------------------------------------

interface CaseCardProps {
  index: number;
  total: number;
  entry: BuilderCase;
  labelError: string | null;
  connectedCount: number;
  isPreviewHit: boolean;
  previewMessage: string | null;
  suggestions: string[];
  disabled: boolean;
  onRename: (next: string) => void;
  onChangeCondition: (next: Condition) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

function CaseCard({
  index,
  total,
  entry,
  labelError,
  connectedCount,
  isPreviewHit,
  previewMessage,
  suggestions,
  disabled,
  onRename,
  onChangeCondition,
  onMove,
  onRemove,
}: CaseCardProps) {
  const [confirming, setConfirming] = useState(false);
  const condition = entry.condition;
  const canCombine = condition.kind === 'rule' || condition.kind === 'always';

  const wrap = (op: 'and' | 'or') =>
    onChangeCondition({
      kind: 'group',
      op,
      items: [condition.kind === 'always' ? newRule() : condition, newRule()],
    });

  return (
    <div
      className={cn(
        'rounded-lg border p-2.5 space-y-2 bg-card',
        isPreviewHit &&
          'border-amber-400 ring-1 ring-amber-300 dark:ring-amber-500/50',
        previewMessage && 'border-red-400',
      )}
      data-testid={`switch-case-${index}`}
    >
      <div className='flex items-center gap-1.5'>
        <span className='text-[11px] text-muted-foreground w-4 shrink-0'>
          {index + 1}.
        </span>
        <DraftInput
          value={entry.label}
          onCommit={onRename}
          disabled={disabled}
          placeholder='branch name'
          aria-label={`Case ${index + 1} branch name`}
          data-testid='switch-case-label'
          className={cn('font-medium flex-1', labelError && 'border-red-500')}
        />
        {!disabled && (
          <>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0'
              aria-label='Move case up'
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              <ArrowUp className='size-3.5' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0'
              aria-label='Move case down'
              disabled={index === total - 1}
              onClick={() => onMove(1)}
            >
              <ArrowDown className='size-3.5' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0 text-muted-foreground hover:text-red-600 dark:text-red-400'
              aria-label={`Remove case ${entry.label}`}
              onClick={() =>
                connectedCount > 0 ? setConfirming(true) : onRemove()
              }
            >
              <Trash2 className='size-3.5' />
            </Button>
          </>
        )}
      </div>
      {labelError && <p className='text-xs text-red-500'>{labelError}</p>}
      {connectedCount > 0 && !labelError && (
        <p className='text-[11px] text-muted-foreground'>
          Connected to {connectedCount} node{connectedCount === 1 ? '' : 's'};
          renaming keeps the connection.
        </p>
      )}
      {confirming && (
        <div
          className='rounded-md border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-950/30 p-2 text-xs flex flex-wrap items-center gap-2'
          role='alert'
        >
          <span className='flex-1'>
            This branch is connected to {connectedCount} node
            {connectedCount === 1 ? '' : 's'}. Remove the case and its
            connection{connectedCount === 1 ? '' : 's'}?
          </span>
          <Button
            type='button'
            variant='destructive'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => {
              setConfirming(false);
              onRemove();
            }}
          >
            Remove
          </Button>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => setConfirming(false)}
          >
            Keep
          </Button>
        </div>
      )}
      <div className='text-[11px] text-muted-foreground'>when</div>
      <ConditionEditor
        condition={condition}
        suggestions={suggestions}
        disabled={disabled}
        depth={0}
        onChange={onChangeCondition}
      />
      {canCombine && !disabled && (
        <div className='flex gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => wrap('and')}
          >
            <Plus className='size-3' /> and also
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => wrap('or')}
          >
            <Plus className='size-3' /> or instead
          </Button>
        </div>
      )}
      {isPreviewHit && (
        <p className='text-[11px] text-amber-700 dark:text-amber-400 font-medium'>
          With the last value this case would be taken.
        </p>
      )}
      {previewMessage && (
        <p className='text-[11px] text-red-600 dark:text-red-400'>
          {previewMessage}
        </p>
      )}
    </div>
  );
}

// --- the editor ------------------------------------------------------------

interface SwitchCasesEditorProps {
  nodeId: string;
  struct: NodeStruct;
  onChange: (cases: SwitchCase[]) => void;
  disabled?: boolean;
  info?: string;
}

const uniqueLabel = (existing: string[], base = 'case'): string => {
  let n = existing.length + 1;
  while (existing.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

export function SwitchCasesEditor({
  nodeId,
  struct,
  onChange,
  disabled = false,
  info,
}: SwitchCasesEditorProps) {
  const { node, nodes, edges, renameSwitchBranch, onEdgesDelete } =
    useNodeStore(
      useShallow((state) => ({
        node: state.nodes.find((n) => n.id === nodeId) as
          | CustomNode
          | undefined,
        nodes: state.nodes,
        edges: state.edges,
        renameSwitchBranch: state.renameSwitchBranch,
        onEdgesDelete: state.onEdgesDelete,
      })),
    );
  const [mode, setMode] = useState<'builder' | 'json'>('builder');

  const config = useMemo(() => getSwitchConfig(struct), [struct]);
  const builder = useMemo(() => casesToBuilder(config.cases), [config.cases]);
  const errors = useMemo(() => validateSwitchConfig(config), [config]);

  const sample = useMemo(
    () => (node ? resolveSwitchInputSample(node, nodes, edges) : undefined),
    [node, nodes, edges],
  );
  // A scalar upstream value (a classifier's label, a count) has no fields:
  // the picker offers the value itself so the case can say `equals "billing"`.
  const suggestions = useMemo(
    () => fieldSuggestionsForSample(sample),
    [sample],
  );
  const preview = useMemo(
    () => previewSwitchSelection(config, sample),
    [config, sample],
  );
  const orphaned = useMemo(
    () => orphanedBranchEdges(struct, edges, nodeId),
    [struct, edges, nodeId],
  );

  const commit = (next: BuilderCase[]) => onChange(builderToCases(next));

  const labelErrorAt = (index: number): string | null =>
    errors.find((error) => error.path === `cases[${index}].label`)?.message ??
    null;

  const upstreamName = useMemo(() => {
    const valuePortId = struct.params?.find((p) => p.field_id === 'value')
      ?.bindable_config?.input_port?.id;
    const incoming = edges.find(
      (edge) => edge.target === nodeId && edge.targetHandle === valuePortId,
    );
    const source = nodes.find((n) => n.id === incoming?.source);
    return (
      source?.data?.struct?.user_config_name ||
      source?.data?.struct?.display_name ||
      null
    );
  }, [struct.params, edges, nodes, nodeId]);

  return (
    <div className='w-full mb-4 px-3' data-testid='switch-cases-editor'>
      <div className='flex items-center justify-between gap-2 mb-1 min-h-[36px]'>
        <div className='flex items-center gap-1'>
          <Label className='text-sm label-required'>Cases</Label>
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 text-muted-foreground cursor-help' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>
                  Cases are tried top to bottom; the first one that matches
                  picks the branch. Each case becomes a labelled handle on the
                  node.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-7 px-2 text-xs'
          data-testid='switch-cases-mode-toggle'
          onClick={() => setMode(mode === 'builder' ? 'json' : 'builder')}
        >
          {mode === 'builder' ? (
            <>
              <Braces className='size-3.5' /> Edit as JSON
            </>
          ) : (
            <>
              <ListChecks className='size-3.5' /> Edit as fields
            </>
          )}
        </Button>
      </div>

      {preview.kind !== 'unknown' && (
        <div
          className={cn(
            'rounded-md px-2 py-1.5 mb-2 text-xs',
            preview.kind === 'fail'
              ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
              : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
          )}
          data-testid='switch-preview'
        >
          {preview.kind === 'fail'
            ? preview.message
            : `With the last value${upstreamName ? ` from ${upstreamName}` : ''}, this Switch would take "${preview.label}"${preview.kind === 'default' ? ' (the default branch)' : ''}.`}
        </div>
      )}

      {mode === 'json' ? (
        <JsonEditorField
          value={config.cases}
          rows={12}
          expectedShape='array'
          disabled={disabled}
          aria-label='Cases as JSON'
          data-testid='switch-cases-json'
          placeholder='[{"label": "approved", "when": {"op": "eq", "left": {"op": "path", "path": ["value", "status"]}, "right": {"op": "literal", "value": "ok"}}}]'
          validate={(parsed) => {
            const cases = parsed as SwitchCase[];
            if (
              !cases.every(
                (entry) =>
                  entry &&
                  typeof entry === 'object' &&
                  typeof (entry as SwitchCase).label === 'string' &&
                  (entry as SwitchCase).when,
              )
            ) {
              return 'Each case needs a "label" and a "when" condition.';
            }
            return (
              validateSwitchConfig({ ...config, cases })[0]?.message ?? null
            );
          }}
          onCommit={(parsed) => onChange(parsed as SwitchCase[])}
        />
      ) : (
        <div className='space-y-2'>
          {builder.map((entry, index) => (
            <CaseCard
              key={index}
              index={index}
              total={builder.length}
              entry={entry}
              labelError={labelErrorAt(index)}
              connectedCount={edgesForBranch(edges, nodeId, entry.label).length}
              isPreviewHit={preview.kind === 'case' && preview.index === index}
              previewMessage={
                preview.kind === 'fail' && preview.caseIndex === index
                  ? preview.message
                  : null
              }
              suggestions={suggestions}
              disabled={disabled}
              onRename={(next) => {
                const trimmed = next.trim();
                if (!trimmed || trimmed === entry.label) return;
                const nextBuilder = builder.map((c, i) =>
                  i === index ? { ...c, label: trimmed } : c,
                );
                const labelErrors = validateSwitchConfig({
                  ...config,
                  cases: builderToCases(nextBuilder),
                }).filter((e) => e.path === `cases[${index}].label`);
                if (labelErrors.length === 0) {
                  renameSwitchBranch(nodeId, entry.label, trimmed);
                }
                commit(nextBuilder);
              }}
              onChangeCondition={(condition) =>
                commit(
                  builder.map((c, i) =>
                    i === index ? { ...c, condition } : c,
                  ),
                )
              }
              onMove={(direction) => {
                const target = index + direction;
                if (target < 0 || target >= builder.length) return;
                const next = [...builder];
                [next[index], next[target]] = [next[target], next[index]];
                commit(next);
              }}
              onRemove={() => {
                const attached = edgesForBranch(edges, nodeId, entry.label);
                if (attached.length > 0) onEdgesDelete(attached);
                commit(builder.filter((_, i) => i !== index));
              }}
            />
          ))}
          {!disabled && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-8 text-xs w-full'
              data-testid='switch-add-case'
              onClick={() =>
                commit([
                  ...builder,
                  {
                    label: uniqueLabel(builder.map((c) => c.label)),
                    condition: newRule(),
                  },
                ])
              }
            >
              <Plus className='size-3.5' /> Add case
            </Button>
          )}
        </div>
      )}

      {errors.filter((e) => !e.path.endsWith('.label')).length > 0 && (
        <ul className='mt-2 space-y-0.5' role='alert'>
          {errors
            .filter((e) => !e.path.endsWith('.label'))
            .slice(0, 3)
            .map((error, i) => (
              <li key={i} className='text-xs text-red-500'>
                {error.message}
              </li>
            ))}
        </ul>
      )}

      {orphaned.length > 0 && (
        <div className='mt-2 rounded-md border border-amber-300 dark:border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs flex flex-wrap items-center gap-2'>
          <span className='flex-1'>
            {orphaned.length} connection{orphaned.length === 1 ? '' : 's'} use a
            branch name that no longer exists (
            {[...new Set(orphaned.map((e) => e.data?.branch_label))].join(', ')}
            ). They will be dropped when you save.
          </span>
          {!disabled && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-6 px-2 text-xs'
              onClick={() => onEdgesDelete(orphaned)}
            >
              Remove now
            </Button>
          )}
        </div>
      )}

      {mode === 'builder' && builder.length > 0 && (
        <p className='mt-2 text-[11px] text-muted-foreground'>
          {builder
            .map(
              (entry) =>
                `${entry.label}: ${describeCondition(entry.condition)}`,
            )
            .join(' · ')}
        </p>
      )}
    </div>
  );
}
